import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import { useCartUI } from '../../context/CartUIContext';
import { useLanguage } from '../../context/LanguageContext';
import { getPublicProductsBySlugs } from '../../lib/productsApi';
import { Product } from '../../data/productModels';
import { AppRoutes } from '../../types';
import { Button } from '../UI/Button';
import { isPurchasable } from '../../lib/pricing';

export const CartDrawer: React.FC = () => {
  const { items, updateQty, removeFromCart, totalQty, clearCart, cartCurrency } = useCart();
  const { isCartOpen, closeCart } = useCartUI();
  const { language, makePath } = useLanguage();
  const navigate = useNavigate();
  const dataLang = language === 'en' ? 'en' : 'zh';

  // Store products fetched from API
  const [products, setProducts] = useState<Record<string, Product | undefined>>({});
  const [, setIsLoadingProducts] = useState(false);

  // Fetch products when cart items change — single batch request
  useEffect(() => {
    if (items.length === 0) return;
    let cancelled = false;
    (async () => {
      setIsLoadingProducts(true);
      const slugs = items.map(i => i.slug);
      const fetched = await getPublicProductsBySlugs(slugs);
      if (!cancelled) {
        setProducts(fetched);
        setIsLoadingProducts(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [items]);

  useEffect(() => {
    document.body.style.overflow = isCartOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isCartOpen]);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeCart();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [closeCart]);

  const { cartProducts, hasInvalidItems } = useMemo(() => {
    let invalid = false;
    const mapped = items.map(item => {
      const product = products[item.slug];
      const valid = isPurchasable(product, cartCurrency);
      if (!valid) invalid = true;
      return { ...item, product, valid };
    });
    return { cartProducts: mapped, hasInvalidItems: invalid };
  }, [items, products, cartCurrency]);

  const handleViewCart = () => {
    closeCart();
    navigate(makePath(AppRoutes.CART));
  };

  const handleCheckout = () => {
    if (hasInvalidItems) return;
    closeCart();
    navigate(makePath(AppRoutes.CHECKOUT));
  };

  const ui = {
    en: {
      title: 'Cart',
      empty: 'Your cart is currently empty.',
      subtotal: 'Subtotal',
      tbd: 'Calculated at checkout',
      viewCart: 'View Cart',
      checkout: 'Checkout',
      clear: 'Clear',
      shop: 'Shop Products',
      unavailable: 'Not available',
    },
    'zh-hk': {
      title: '購物車',
      empty: '您的購物車目前是空的。',
      subtotal: '小計',
      tbd: '於結帳時計算',
      viewCart: '查看購物車',
      checkout: '前往結帳',
      clear: '清空',
      shop: '去逛逛',
      unavailable: '無法購買',
    },
  };

  const t = ui[language as keyof typeof ui] || ui['en'];

  return (
    <>
      <div
        className={`fixed inset-0 bg-black/40 z-[60] transition-opacity duration-300 backdrop-blur-[2px] ${
          isCartOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={closeCart}
        aria-hidden="true"
      />

      <div
        className={`fixed top-0 right-0 h-full w-full max-w-[420px] bg-natuvia-primary z-[70] shadow-2xl transition-transform duration-300 ease-out transform flex flex-col ${
          isCartOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        onClick={e => e.stopPropagation()}
      >
        <div className="flex-none flex items-center justify-between p-6 border-b border-natuvia-text/10 bg-natuvia-primary z-10">
          <h2 className="text-xl font-bold text-natuvia-text flex items-center gap-2">
            {t.title}
            {totalQty > 0 && (
              <span className="bg-natuvia-cta text-white text-xs px-2 py-0.5 rounded-full">
                {totalQty}
              </span>
            )}
          </h2>
          <button
            onClick={closeCart}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-natuvia-text/10 text-natuvia-text transition-colors"
          >
            ✕
          </button>
        </div>

        <div className="flex-grow overflow-y-auto p-6">
          {cartProducts.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center opacity-50 space-y-4">
              <span className="text-4xl">🛒</span>
              <p className="text-lg font-medium">{t.empty}</p>
              <Button variant="secondary" onClick={closeCart} className="mt-4">
                {t.shop}
              </Button>
            </div>
          ) : (
            <div className="space-y-6">
              {cartProducts.map(({ slug, qty, product, valid }) => (
                <div
                  key={slug}
                  className={`flex gap-4 group ${!valid ? 'opacity-70 grayscale-[0.5]' : ''}`}
                >
                  <div className="w-20 h-20 bg-white rounded-lg border border-natuvia-text/10 flex-shrink-0 overflow-hidden flex items-center justify-center relative">
                    {product?.cover ? (
                      <img
                        src={product.cover}
                        alt={product.name[dataLang]}
                        width={80}
                        height={80}
                        loading="lazy"
                        decoding="async"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="text-xs text-natuvia-text/20 font-mono">IMG</span>
                    )}
                  </div>

                  <div className="flex-grow flex flex-col justify-between py-1">
                    <div>
                      <h3 className="font-bold text-natuvia-text leading-tight line-clamp-2 text-sm md:text-base">
                        {product ? product.name[dataLang] : slug}
                      </h3>
                      {!valid && (
                        <p className="text-xs font-bold text-red-500 mt-1">
                          {t.unavailable} {cartCurrency}
                        </p>
                      )}
                      <p className="text-xs text-natuvia-text/60 mt-1">
                        {product?.primaryProtein || 'Unknown'}
                      </p>
                    </div>

                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center border border-natuvia-text/20 rounded bg-white h-7">
                        <button
                          onClick={() => updateQty(slug, qty - 1)}
                          className="w-7 h-full flex items-center justify-center hover:bg-natuvia-text/5 text-natuvia-text/80 font-bold"
                        >
                          -
                        </button>
                        <span className="text-xs font-mono w-8 text-center">{qty}</span>
                        <button
                          onClick={() => updateQty(slug, qty + 1)}
                          className="w-7 h-full flex items-center justify-center hover:bg-natuvia-text/5 text-natuvia-text/80 font-bold disabled:opacity-30 disabled:cursor-not-allowed"
                          disabled={!valid}
                        >
                          +
                        </button>
                      </div>

                      <button
                        onClick={() => removeFromCart(slug)}
                        className="text-xs text-natuvia-text/40 hover:text-red-500 underline transition-colors"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                </div>
              ))}

              <div className="pt-2 text-right">
                <button
                  onClick={clearCart}
                  className="text-xs text-natuvia-text/40 hover:text-natuvia-text underline transition-colors"
                >
                  {t.clear}
                </button>
              </div>
            </div>
          )}
        </div>

        {cartProducts.length > 0 && (
          <div className="flex-none p-6 border-t border-natuvia-text/10 bg-natuvia-alt/90 backdrop-blur-md space-y-4 z-10">
            <div className="flex justify-between items-center text-lg font-bold text-natuvia-text">
              <span>{t.subtotal}</span>
              <span className="text-sm opacity-60">{t.tbd}</span>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Button
                variant="secondary"
                className="w-full justify-center !text-sm"
                onClick={handleViewCart}
              >
                {t.viewCart}
              </Button>
              <Button
                variant="primary"
                className="w-full justify-center !text-sm shadow-lg"
                onClick={handleCheckout}
                disabled={hasInvalidItems}
              >
                {t.checkout}
              </Button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};
